import type { MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiamondExclamation } from '@fortawesome/pro-regular-svg-icons/faDiamondExclamation';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './account-info-summary.messages';
import { WwexEditLink } from './wwex-edit-link';
import style from './account-info-summary.module.css';

const EMPTY_VALUES = ['null', 'undefined', '<null>', '<undefined>'];

const isMissingValue = (value: string) => {
  const trimmedValue = value.trim();
  return !trimmedValue || EMPTY_VALUES.includes(trimmedValue.toLowerCase());
};

type AccountInfoFieldProps = {
  value: string;
  requiredMessage: MessageDescriptor;
};

const AccountInfoField = ({ value, requiredMessage }: AccountInfoFieldProps) =>
  isMissingValue(value) ? (
    <FlexRow className={style.fieldError} gap={Gap.Space4}>
      <FontAwesomeIcon className={style.fieldErrorIcon} icon={faDiamondExclamation} />
      <Text className={style.fieldErrorText} textType={TextType.Caption} value={requiredMessage} />
    </FlexRow>
  ) : (
    <Text className={style.readOnlyText} textType={TextType.Caption} value={value.trim()} />
  );

type AccountInfoSummaryProps = {
  contactFullName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  hasError?: boolean;
  isDisabled?: boolean;
  onEdit: () => void;
};

export const AccountInfoSummary = ({
  contactFullName,
  companyName,
  email,
  phoneNumber,
  hasError = false,
  isDisabled = false,
  onEdit,
}: AccountInfoSummaryProps) => (
  <FlexColumn className={clsx(style.container, hasError && style.containerError)} gap={Gap.Space4}>
    <FlexRow className={style.headerRow} gap={Gap.Space8}>
      <Text isBold className={style.sectionTitle} textType={TextType.Label} value={messages.sectionTitle} />
      <WwexEditLink isDisabled={isDisabled} onClick={onEdit} />
    </FlexRow>
    <FlexColumn gap={Gap.Space4}>
      <AccountInfoField requiredMessage={messages.contactFullNameRequired} value={contactFullName} />
      <AccountInfoField requiredMessage={messages.companyNameRequired} value={companyName} />
      <AccountInfoField requiredMessage={messages.emailRequired} value={email} />
      <AccountInfoField requiredMessage={messages.phoneNumberRequired} value={phoneNumber} />
    </FlexColumn>
  </FlexColumn>
);
